import React, { Context, createContext, FC, useCallback, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { IServerSidePaginatedFetcherParams } from './Fetcher';
import { IPage } from '../resources/models/IPage';
import { IResponse } from '../resources/models/IResponse';
import { captureException } from '../sentry';

interface ILazyContext<DataType, IdentifierType extends string> {
  requestData: (identifier: IdentifierType) => void;
  data: Record<IdentifierType, DataType>;
  failedData: IdentifierType[];
}

export const defaultLazyContext: ILazyContext<any, any> = {
  requestData: () => void 0,
  data: {},
  failedData: [],
};

export const generateLazyContext = <DataType extends unknown, IdentifierType extends string>() => {
  return createContext<ILazyContext<DataType, IdentifierType>>(defaultLazyContext);
};

export const generateLazyContextProvider = <DataType extends unknown, IdentifierType extends string>(
  LazyContext: Context<ILazyContext<DataType, IdentifierType>>,
  getIdentifier: (item: DataType) => IdentifierType,
  useDataFetcher: () => (
    identifiers: IdentifierType[],
    pagination: IServerSidePaginatedFetcherParams
  ) => Promise<IResponse<IPage<DataType>>>
) => {
  const Provider: FC = ({ children }) => {
    const [loadedData, setLoadedData] = useState<Record<IdentifierType, DataType>>(
      {} as Record<IdentifierType, DataType>
    );
    const [failedData, setFailedData] = useState<IdentifierType[]>([]);

    const requestedDataRef = useRef<IdentifierType[]>([]);
    const loadedDataIdsRef = useRef<IdentifierType[]>([]);
    loadedDataIdsRef.current = Object.keys(loadedData) as IdentifierType[];

    const dataGetter = useDataFetcher();

    const loadData = useCallback(async () => {
      const ids = requestedDataRef.current;
      requestedDataRef.current = [];

      if (ids.length === 0) return;
      try {
        /**
         * if attempting to get an item that failed previously, remove that from the list of failed data
         */
        setFailedData((prevState) => {
          if (prevState.some((failed) => ids.includes(failed))) {
            return prevState.filter((failed) => !ids.includes(failed));
          }
          return prevState;
        });

        const result = await dataGetter(ids, { page: 1, limit: ids.length });

        setLoadedData((loadedUsers) =>
          result.payload.data.reduce(
            (records, record) => ({
              ...records,
              [getIdentifier(record)]: record,
            }),
            loadedUsers
          )
        );

        const missingItems = ids.filter((id) => !result.payload.data.find((item) => getIdentifier(item) === id));

        if (missingItems.length > 0) {
          setFailedData((prevState) => [...prevState, ...missingItems.filter((id) => !prevState.includes(id))]);
        }
      } catch (e) {
        /**
         * Report that to Sentry, but no need to show error.
         */
        captureException(e);
        setFailedData((prevState) => [...prevState, ...ids.filter((id) => !prevState.includes(id))]);
        return;
      }
    }, [dataGetter]);

    const loadDataDebounced = useDebouncedCallback(loadData, 100);

    const requestData = useCallback(
      (id: IdentifierType) => {
        if (id && !loadedDataIdsRef.current.includes(id) && !requestedDataRef.current.includes(id)) {
          requestedDataRef.current.push(id);
          loadDataDebounced();
        }
      },
      [loadDataDebounced]
    );

    const ctxValue = useMemo<ILazyContext<DataType, IdentifierType>>(() => {
      return {
        data: loadedData,
        requestData,
        failedData,
      };
    }, [loadedData, requestData, failedData]);

    return <LazyContext.Provider value={ctxValue}>{children}</LazyContext.Provider>;
  };

  return Provider;
};
